import { Share2 } from "lucide-react";
import React from "react";
import { Dialog, DialogContent, DialogTrigger } from "~/components/ui/dialog";
import { formatDate } from "~/lib/helpers/dateFormatHelper";
import { StarRating } from "~/lib/utils/GoogleStarGenerater";
import { ShareDialogContent } from "../common/ShareModalContent";
import { getFlagUrl } from "custom-config/region.configs";
import { getCountryCode } from "~/lib/helpers/constants";
import { Link } from "@remix-run/react";
import { Separator } from "~/components/ui/separator";

// Props definition
type TestimonialCardProps = {
    profilePhoto: string;
    authorName: string;
    starRating: number;
    createdDate: string;
    rating: string | number;
    comment: string;
    country: string;
    authorUrl: string
    showSeparator?: boolean;
};


// Main component
const TestimonialCard = ({
    review,
    showSeparator = true, // ✅ default true
}: {
    review: TestimonialCardProps;
    showSeparator?: boolean;
}) => {
    const {
        profilePhoto,
        authorName,
        starRating,
        createdDate,
        authorUrl,
        comment,
        country,
    } = review;

    const countryCode = getCountryCode(country);
    const flag = countryCode ? getFlagUrl(countryCode) : null;

    const IdentitySection = () => (
        <div className='flex items-center space-x-3'>
            <img
                src={profilePhoto}
                alt={`${authorName} profile`}
                width={48}
                height={48}
                loading="lazy"
                decoding="async"
                className='w-12 h-12 rounded-full object-cover border'
            />
            <div>
                <p className='font-semibold text-lg text-oh-black group-hover:text-oh-primary transition-colors'>{authorName}</p>
                <div className='text-sm text-gray-500'>
                    {starRating} reviews
                </div>
            </div>
        </div>
    );

    return (
        <div className={`py-6 relative group transition-colors flex flex-col ${authorUrl ? 'hover:bg-gray-50/50 cursor-pointer' : ''}`}>
            <div className='px-4 mx-auto w-full'>
                <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
                    <div className="flex-1">
                        {authorUrl ? (
                            <Link
                                to={authorUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-block after:absolute after:inset-0 after:z-0"
                                aria-label={`Read more reviews from ${authorName}`}
                            >
                                <IdentitySection />
                            </Link>
                        ) : (
                            <IdentitySection />
                        )}
                    </div>
                    <div className='mt-2 sm:mt-0 text-sm text-gray-500 shrink-0 relative z-10 pointer-events-none'>
                        {formatDate(createdDate, "MMM D, YYYY")}
                    </div>
                </div>

                <div className='flex items-center mt-3 space-x-2 relative z-10 pointer-events-none'>
                    <StarRating rating={starRating} />
                    <span className='text-oh-black text-base font-semibold'>{starRating.toFixed(1)}</span>
                </div>
                <div
                    className='mt-3 text-oh-black font-normal leading-relaxed text-base relative z-10 pointer-events-none'
                    dangerouslySetInnerHTML={{ __html: comment }}
                />
            </div>

            <div className='flex items-center px-4 mt-5 space-x-7 relative z-[11]'>
                <Dialog>
                    <DialogTrigger asChild>
                        <button
                            aria-label="Share this review"
                            type="button"
                            className="hover:opacity-70 transition-opacity p-1 -m-1"
                        >
                            <Share2
                                size={20}
                                className='text-gray-800'
                                aria-hidden="true"
                            />
                        </button>
                    </DialogTrigger>
                    <DialogContent className="px-4 py-0 gap-2">
                        <ShareDialogContent
                            url={authorUrl || (typeof window !== 'undefined' ? window.location.href : '')}
                            title={"Share this Review"}
                        />
                    </DialogContent>
                </Dialog>

                <span className='flex items-center opacity-90 pointer-events-none'>
                    {flag && (
                        <img
                            src={flag}
                            alt={`${country} flag`}
                            width={28}
                            height={28}
                            loading="lazy"
                            decoding="async"
                            className='mr-2 w-7 h-7 rounded-full border'
                        />
                    )}
                    <span className='text-sm text-gray-600'>{country}</span>
                </span>
            </div>

            {showSeparator && (
                <div className="px-4 mt-6 relative z-10">
                    <Separator className='w-full' />
                </div>
            )}
        </div>
    );
};

export default TestimonialCard;
